// src/utils/reporteGasConsolidadoHTML.js
// ⭐ Genera el HTML del reporte consolidado (por zona + por delivery)
// Con el mismo diseño que el PDF de referencia + comisión

import { obtenerDatosEmpresa } from './tenantConfig';
import {
  calcularReportePorZona,
  calcularReportePorDelivery,
  calcularResumenEjecutivo,
  TARIFAS_COMISION,
  fmt,
  fmtBs,
  fmtUsd,
  fmtPct
} from './reporteGasConsolidado';

const getEmpresa = () => {
  try {
    const d = obtenerDatosEmpresa();
    return {
      nombre: d.nombreComercial || d.nombre || 'EXTRA GAS',
      rif: d.rif || '',
      direccion: d.direccion || '',
      ciudad: d.ciudad || '',
      logoBase64: d.logoBase64 || null
    };
  } catch {
    return { nombre: 'EXTRA GAS', rif: '', direccion: '', ciudad: '', logoBase64: null };
  }
};

export const generarHTMLReporteConsolidado = (
  pedidos = [],
  asignaciones = [],
  deliveries = [],
  filtros = {}
) => {
  const empresa = getEmpresa();
  const porZona = calcularReportePorZona(pedidos);
  const porDelivery = calcularReportePorDelivery(pedidos, asignaciones, deliveries);
  const resumen = calcularResumenEjecutivo(pedidos, asignaciones, deliveries);

  const fechaActual = new Date().toLocaleString('es-VE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  const filtrosTexto = [];
  if (filtros.desde) filtrosTexto.push(`Desde: ${filtros.desde}`);
  if (filtros.hasta) filtrosTexto.push(`Hasta: ${filtros.hasta}`);
  if (filtros.zona) filtrosTexto.push(`Zona: ${filtros.zona}`);
  if (filtros.metodoPago) filtrosTexto.push(`Método: ${filtros.metodoPago}`);
  if (filtros.delivery) filtrosTexto.push(`Delivery: ${filtros.delivery}`);

  const maxMontoZona = porZona.zonas.length > 0
    ? Math.max(...porZona.zonas.map(z => z.montoBs))
    : 1;

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<title>Reporte Consolidado de Ventas por Zona - ${empresa.nombre}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: -apple-system, 'Segoe UI', Arial, sans-serif;
    background: #fff;
    color: #1a2a4a;
    padding: 24px;
    font-size: 11px;
  }
  .container { max-width: 1200px; margin: 0 auto; }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
    border-bottom: 3px solid #1a2a4a;
    margin-bottom: 20px;
  }
  .logo-area { display: flex; align-items: center; gap: 12px; }
  .logo-img { height: 55px; width: auto; object-fit: contain; }
  .empresa-nombre { font-size: 17px; font-weight: 900; color: #1a2a4a; }
  .empresa-slogan { font-size: 10px; color: #64748b; margin-top: 2px; }
  .header-info { text-align: right; font-size: 10px; color: #475569; line-height: 1.5; }

  h1 {
    font-size: 22px;
    font-weight: 900;
    color: #0f172a;
    margin-bottom: 6px;
    letter-spacing: -0.3px;
  }
  .subtitulo {
    font-size: 11px;
    color: #64748b;
    margin-bottom: 20px;
  }
  .filtros-info {
    display: inline-block;
    background: #f1f5f9;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 10px;
    color: #475569;
    margin-bottom: 20px;
  }

  .resumen-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 28px;
  }
  .resumen-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-left: 4px solid #1e40af;
    border-radius: 8px;
    padding: 14px 16px;
  }
  .resumen-card.verde { border-left-color: #16a34a; }
  .resumen-card.naranja { border-left-color: #ea580c; }
  .resumen-card.morado { border-left-color: #7c3aed; }
  .resumen-card.amber { border-left-color: #f59e0b; }
  .resumen-label {
    font-size: 9px;
    font-weight: 900;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #64748b;
    margin-bottom: 6px;
  }
  .resumen-valor {
    font-size: 18px;
    font-weight: 900;
    color: #0f172a;
    line-height: 1.1;
  }
  .resumen-detalle {
    font-size: 10px;
    color: #64748b;
    margin-top: 4px;
  }

  .section-title {
    font-size: 14px;
    font-weight: 900;
    color: #0f172a;
    margin-bottom: 14px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e2e8f0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .section-title::before {
    content: '';
    width: 4px;
    height: 16px;
    background: #1e40af;
    border-radius: 2px;
  }

  table.zonas {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
    margin-bottom: 32px;
  }
  table.zonas thead {
    background: #1e40af;
    color: white;
  }
  table.zonas thead th {
    padding: 10px 8px;
    text-align: left;
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  table.zonas thead th.right { text-align: right; }
  table.zonas thead th.center { text-align: center; }
  table.zonas tbody td {
    padding: 9px 8px;
    border-bottom: 1px solid #e2e8f0;
    vertical-align: middle;
  }
  table.zonas tbody tr:nth-child(even) { background: #f8fafc; }
  table.zonas tbody td.right { text-align: right; }
  table.zonas tbody td.center { text-align: center; }
  table.zonas tbody td.zona-nombre { font-weight: 700; }
  table.zonas tbody td.monto { font-weight: 900; color: #0f172a; font-size: 12px; }
  table.zonas tbody td.pct { color: #64748b; font-weight: 700; }
  table.zonas tfoot {
    background: #0f172a;
    color: white;
    font-weight: 900;
  }
  table.zonas tfoot td {
    padding: 12px 8px;
    font-size: 12px;
  }
  table.zonas tfoot td.right { text-align: right; }
  table.zonas tfoot td.center { text-align: center; }

  .barra-wrap {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .barra-bg {
    flex: 1;
    height: 6px;
    background: #e2e8f0;
    border-radius: 3px;
    overflow: hidden;
    min-width: 40px;
  }
  .barra-fill {
    height: 100%;
    background: linear-gradient(90deg, #1e40af 0%, #3b82f6 100%);
    border-radius: 3px;
  }
  .barra-fill.naranja {
    background: linear-gradient(90deg, #ea580c 0%, #f97316 100%);
  }

  .grafico-barras {
    background: #f8fafc;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 32px;
  }
  .barra-item { margin-bottom: 12px; }
  .barra-item:last-child { margin-bottom: 0; }
  .barra-item-label {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    font-weight: 700;
    color: #334155;
    margin-bottom: 5px;
  }
  .barra-item-label .monto { font-weight: 900; color: #0f172a; }
  .barra-h {
    height: 22px;
    background: #e2e8f0;
    border-radius: 4px;
    overflow: hidden;
  }
  .barra-h-fill {
    height: 100%;
    background: linear-gradient(90deg, #1e40af 0%, #3b82f6 100%);
    border-radius: 4px;
    transition: width 0.3s;
  }
  .barra-h-fill.verde { background: linear-gradient(90deg, #16a34a 0%, #22c55e 100%); }
  .barra-h-fill.gris { background: linear-gradient(90deg, #64748b 0%, #94a3b8 100%); }

  .delivery-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin-bottom: 32px;
  }
  .delivery-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
  }
  .delivery-head {
    background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
    color: white;
    padding: 12px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .delivery-head.gris {
    background: linear-gradient(135deg, #475569 0%, #64748b 100%);
  }
  .delivery-nombre { font-size: 13px; font-weight: 900; }
  .delivery-badge {
    background: rgba(255,255,255,0.2);
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 9px;
    font-weight: 900;
    letter-spacing: 0.3px;
    text-transform: uppercase;
  }
  .delivery-body { padding: 12px 16px; }
  .delivery-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e2e8f0;
  }
  .delivery-stat { text-align: center; }
  .delivery-stat-label {
    font-size: 8px;
    font-weight: 900;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 3px;
  }
  .delivery-stat-value {
    font-size: 14px;
    font-weight: 900;
    color: #0f172a;
  }
  .delivery-zonas-title {
    font-size: 9px;
    font-weight: 900;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
  }
  .delivery-zona-row {
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    padding: 3px 0;
    color: #334155;
  }
  .delivery-zona-row .nombre { font-weight: 700; }
  .delivery-zona-row .monto { font-weight: 900; color: #0f172a; }

  .delivery-comision {
    background: #fffbeb;
    border-top: 1px solid #fde68a;
    padding: 10px 16px;
  }
  .delivery-comision-title {
    font-size: 9px;
    font-weight: 900;
    color: #b45309;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
  }
  .delivery-comision-row {
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: #78350f;
    padding: 2px 0;
  }
  .delivery-comision-total {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 900;
    color: #92400e;
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px dashed #fcd34d;
  }
  .delivery-cilindros {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 4px;
    margin-top: 8px;
  }
  .delivery-cilindro {
    background: #fff;
    border: 1px solid #fde68a;
    border-radius: 4px;
    padding: 4px;
    text-align: center;
  }
  .delivery-cilindro .kg {
    font-size: 8px;
    font-weight: 900;
    color: #92400e;
    text-transform: uppercase;
  }
  .delivery-cilindro .cant {
    font-size: 12px;
    font-weight: 900;
    color: #78350f;
  }

  .delivery-subtotal {
    background: #0f172a;
    color: white;
    padding: 8px 16px;
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    font-weight: 900;
  }

  table.comisiones {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
    margin-bottom: 32px;
  }
  table.comisiones thead {
    background: #f59e0b;
    color: white;
  }
  table.comisiones thead th {
    padding: 10px 8px;
    text-align: left;
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  table.comisiones thead th.right { text-align: right; }
  table.comisiones thead th.center { text-align: center; }
  table.comisiones tbody td {
    padding: 9px 8px;
    border-bottom: 1px solid #e2e8f0;
  }
  table.comisiones tbody tr:nth-child(even) { background: #fffbeb; }
  table.comisiones tbody td.right { text-align: right; }
  table.comisiones tbody td.center { text-align: center; }
  table.comisiones tbody td.delivery-nombre { font-weight: 700; }
  table.comisiones tbody td.comision { font-weight: 900; color: #b45309; font-size: 12px; }
  table.comisiones tfoot {
    background: #0f172a;
    color: white;
    font-weight: 900;
  }
  table.comisiones tfoot td {
    padding: 12px 8px;
    font-size: 12px;
  }
  table.comisiones tfoot td.right { text-align: right; }
  table.comisiones tfoot td.center { text-align: center; }
  table.comisiones tfoot td.comision { color: #fbbf24; }

  .footer {
    margin-top: 40px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
    font-size: 9px;
    color: #94a3b8;
    text-align: center;
  }

  @media print {
    body { padding: 0; }
    .no-print { display: none; }
    .delivery-card { break-inside: avoid; }
    @page { margin: 1.2cm; size: A4; }
  }
</style>
</head>
<body>
<div class="container">

  <div class="header">
    <div class="logo-area">
      ${empresa.logoBase64 ? `<img src="${empresa.logoBase64}" class="logo-img" onerror="this.style.display='none'">` : ''}
      <div>
        <div class="empresa-nombre">${empresa.nombre}</div>
        <div class="empresa-slogan">Reporte Consolidado de Ventas</div>
      </div>
    </div>
    <div class="header-info">
      RIF: ${empresa.rif}<br>
      ${empresa.direccion || ''}<br>
      ${empresa.ciudad || ''}
    </div>
  </div>

  <h1>Resumen Ejecutivo de Rendimiento</h1>
  <div class="subtitulo">Generado: ${fechaActual}</div>
  ${filtrosTexto.length > 0
    ? `<div class="filtros-info">🔍 Filtros: ${filtrosTexto.join(' · ')}</div>`
    : ''}

  <div class="resumen-grid">
    <div class="resumen-card">
      <div class="resumen-label">Monto Consumido</div>
      <div class="resumen-valor">${fmtBs(resumen.montoConsumidoBs)}</div>
      <div class="resumen-detalle">Total acumulado de ventas</div>
    </div>
    <div class="resumen-card verde">
      <div class="resumen-label">Clientes Únicos</div>
      <div class="resumen-valor">${fmt(resumen.clientesUnicos, 0)}</div>
      <div class="resumen-detalle">${fmt(resumen.totalCompras, 0)} compras registradas</div>
    </div>
    <div class="resumen-card amber">
      <div class="resumen-label">Comisión Total</div>
      <div class="resumen-valor">${fmtUsd(resumen.comisionTotal || 0)}</div>
      <div class="resumen-detalle">Comisión a pagar a deliveries</div>
    </div>
    <div class="resumen-card naranja">
      <div class="resumen-label">Zona Principal</div>
      <div class="resumen-valor">${resumen.zonaPrincipal ? resumen.zonaPrincipal.nombre : '—'}</div>
      <div class="resumen-detalle">
        ${resumen.zonaPrincipal
          ? `${fmtBs(resumen.zonaPrincipal.montoBs)} (${fmtPct(resumen.zonaPrincipal.pct)})`
          : 'Sin datos'}
      </div>
    </div>
    <div class="resumen-card morado">
      <div class="resumen-label">Líder Comercial</div>
      <div class="resumen-valor">${resumen.liderComercial ? resumen.liderComercial.nombre : '—'}</div>
      <div class="resumen-detalle">
        ${resumen.liderComercial
          ? `${fmtBs(resumen.liderComercial.montoBs)} (${fmtPct(resumen.liderComercial.pct)})`
          : 'Sin datos'}
      </div>
    </div>
  </div>

  <h2 class="section-title">Distribución General por Zona Geográfica</h2>
  <table class="zonas">
    <thead>
      <tr>
        <th>Zona Geográfica</th>
        <th class="center">Clientes Únicos</th>
        <th class="center">% Clientes</th>
        <th class="right">Monto Consumido</th>
        <th class="center">% Monto</th>
        <th class="center">Compras</th>
        <th class="center">Ticket Prom.</th>
      </tr>
    </thead>
    <tbody>
      ${porZona.zonas.length === 0
        ? `<tr><td colspan="7" class="center" style="padding:24px;color:#94a3b8">No hay datos</td></tr>`
        : porZona.zonas.map((z) => `
          <tr>
            <td class="zona-nombre">${z.zona}</td>
            <td class="center">${fmt(z.clientesUnicos, 0)} <span style="color:#94a3b8">(${fmt(z.compras, 0)} compras)</span></td>
            <td class="center pct">${fmtPct(z.pctClientes)}</td>
            <td class="right monto">${fmtBs(z.montoBs)}</td>
            <td class="center pct">${fmtPct(z.pctMonto)}</td>
            <td class="center">${fmt(z.compras, 0)}</td>
            <td class="center">${fmtBs(z.ticketPromedio)}</td>
          </tr>
        `).join('')}
    </tbody>
    <tfoot>
      <tr>
        <td>TOTAL GENERAL</td>
        <td class="center">${fmt(porZona.totales.clientesUnicos, 0)} (${fmt(porZona.totales.totalCompras, 0)} reg.)</td>
        <td class="center">100%</td>
        <td class="right">${fmtBs(porZona.totales.montoBs)}</td>
        <td class="center">100%</td>
        <td class="center">${fmt(porZona.totales.totalCompras, 0)}</td>
        <td class="center">—</td>
      </tr>
    </tfoot>
  </table>

  <h2 class="section-title">Comparativo de Ingresos por Zona Geográfica</h2>
  <div class="grafico-barras">
    ${porZona.zonas.slice(0, 8).map((z, i) => {
      const ancho = (z.montoBs / maxMontoZona) * 100;
      const clase = i === 0 ? '' : (i === 1 ? 'naranja' : 'gris');
      return `
        <div class="barra-item">
          <div class="barra-item-label">
            <span>${z.zona}</span>
            <span class="monto">${fmtBs(z.montoBs)}</span>
          </div>
          <div class="barra-h">
            <div class="barra-h-fill ${clase}" style="width: ${ancho}%"></div>
          </div>
        </div>
      `;
    }).join('')}
  </div>

  <h2 class="section-title">Análisis por Fuerza de Ventas (Delivery)</h2>
  <div class="resumen-detalle" style="margin-bottom:14px;font-size:11px">
    Fuerza Comercial: <strong>${porDelivery.deliveries.length} repartidor${porDelivery.deliveries.length === 1 ? '' : 'es'}</strong>
    · Cobertura: <strong>${new Set(porDelivery.deliveries.flatMap(d => d.zonas)).size} zonas</strong>
  </div>

  <div class="delivery-grid">
    ${porDelivery.deliveries.length === 0
      ? `<div style="padding:24px;color:#94a3b8;text-align:center;grid-column:1/-1">No hay deliveries con pedidos asignados</div>`
      : porDelivery.deliveries.map((d, i) => `
        <div class="delivery-card">
          <div class="delivery-head ${i % 2 === 1 ? 'gris' : ''}">
            <div class="delivery-nombre">${d.nombre}</div>
            <div class="delivery-badge">${d.zonasCount} zona${d.zonasCount === 1 ? '' : 's'}</div>
          </div>
          <div class="delivery-body">
            <div class="delivery-stats">
              <div class="delivery-stat">
                <div class="delivery-stat-label">Pedidos</div>
                <div class="delivery-stat-value">${fmt(d.pedidos, 0)}</div>
              </div>
              <div class="delivery-stat">
                <div class="delivery-stat-label">Clientes</div>
                <div class="delivery-stat-value">${fmt(d.clientesUnicos, 0)}</div>
              </div>
              <div class="delivery-stat">
                <div class="delivery-stat-label">Ticket</div>
                <div class="delivery-stat-value">${fmtBs(d.ticketPromedio)}</div>
              </div>
            </div>
            <div class="delivery-zonas-title">Desglose por Zona</div>
            ${d.zonasDetalle.map((z) => `
              <div class="delivery-zona-row">
                <span class="nombre">${z.zona}</span>
                <span>${fmt(z.clientesUnicos, 0)} cli.</span>
                <span class="monto">${fmtBs(z.montoBs)}</span>
              </div>
            `).join('')}
          </div>

          <div class="delivery-comision">
            <div class="delivery-comision-title">Comisión por Cilindros</div>
            ${Object.entries(TARIFAS_COMISION).map(([kg, tarifa]) => `
              <div class="delivery-comision-row">
                <span>Cilindro ${kg} kg × ${d.cilindrosPorKg[kg] || 0}</span>
                <span>${fmtUsd((d.cilindrosPorKg[kg] || 0) * tarifa)}</span>
              </div>
            `).join('')}
            <div class="delivery-comision-total">
              <span>TOTAL COMISIÓN</span>
              <span>${fmtUsd(d.comision)}</span>
            </div>
            <div class="delivery-cilindros">
              ${Object.entries(TARIFAS_COMISION).map(([kg]) => `
                <div class="delivery-cilindro">
                  <div class="kg">${kg} kg</div>
                  <div class="cant">${d.cilindrosPorKg[kg] || 0}</div>
                </div>
              `).join('')}
            </div>
          </div>

          <div class="delivery-subtotal">
            <span>SUBTOTAL ${d.nombre.toUpperCase()}</span>
            <span>${fmt(d.pedidos, 0)} ped. · ${fmtBs(d.montoBs)} · ${fmtUsd(d.comision)}</span>
          </div>
        </div>
      `).join('')}
  </div>

  <h2 class="section-title">Reporte de Comisiones por Delivery</h2>
  <table class="comisiones">
    <thead>
      <tr>
        <th>Delivery</th>
        <th class="center">Pedidos</th>
        <th class="center">10 kg</th>
        <th class="center">18 kg</th>
        <th class="center">27 kg</th>
        <th class="center">43 kg</th>
        <th class="right">Comisión USD</th>
      </tr>
    </thead>
    <tbody>
      ${porDelivery.deliveries.length === 0
        ? `<tr><td colspan="7" class="center" style="padding:24px;color:#94a3b8">No hay datos</td></tr>`
        : porDelivery.deliveries.map((d) => `
          <tr>
            <td class="delivery-nombre">${d.nombre}</td>
            <td class="center">${fmt(d.pedidos, 0)}</td>
            <td class="center">${fmt(d.cilindrosPorKg[10] || 0, 0)}</td>
            <td class="center">${fmt(d.cilindrosPorKg[18] || 0, 0)}</td>
            <td class="center">${fmt(d.cilindrosPorKg[27] || 0, 0)}</td>
            <td class="center">${fmt(d.cilindrosPorKg[43] || 0, 0)}</td>
            <td class="right comision">${fmtUsd(d.comision)}</td>
          </tr>
        `).join('')}
    </tbody>
    <tfoot>
      <tr>
        <td>TOTAL GENERAL</td>
        <td class="center">${fmt(porDelivery.totales.pedidos, 0)}</td>
        <td class="center">${fmt(porDelivery.totales.cilindrosPorKg[10], 0)}</td>
        <td class="center">${fmt(porDelivery.totales.cilindrosPorKg[18], 0)}</td>
        <td class="center">${fmt(porDelivery.totales.cilindrosPorKg[27], 0)}</td>
        <td class="center">${fmt(porDelivery.totales.cilindrosPorKg[43], 0)}</td>
        <td class="right comision">${fmtUsd(porDelivery.totales.comision)}</td>
      </tr>
    </tfoot>
  </table>

  <div class="footer">
    ${empresa.nombre} · Reporte Consolidado de Ventas por Zona y Delivery · Sistema SystemJJ
  </div>
</div>
</body>
</html>`;
};